// @ts-check
// pwa/lib/sw-update.js — service-worker "new version available" banner.
//
// The build script hashes shell assets into the SW cache key. When the user
// loads the app while a new SW is waiting, the SW activates (skipWaiting +
// clients.claim are already in sw.js) and fires `controllerchange` on the
// existing tab. Without a prompt, the new bundle is live but the old DOM is
// still on screen — confusing on long-lived tabs.
//
// The banner explicitly hands the refresh decision to the user (web.dev
// recommends this over silent reload):
//   https://web.dev/learn/pwa/workbox#avoid-skipwaiting-pitfalls
//
// Styling lives in `pwa/css/main.css` (@layer components — `.sw-update*`).
//
// Boot suppression: the FIRST controlled SW on a fresh install also fires
// `controllerchange` (the install-then-claim handshake). We don't want to
// nag on first load, so we ignore the event if there was no controller at
// boot time (a clean install). Subsequent activations always have one.

export function watchForUpdate() {
  if (!('serviceWorker' in navigator)) return;

  const hadControllerAtBoot = !!navigator.serviceWorker.controller;
  let shown = false;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerAtBoot) return;
    if (shown) return;
    shown = true;
    showBanner();
  });
}

// Chunk-load recovery. A deploy purges the old content-hashed view chunks from
// the server, and the new SW's `activate` deletes the previous SW cache that
// held them. A long-open page that then lazily import()s a not-yet-loaded view
// rejects with a "failed to fetch dynamically imported module" error. We reload
// once — sessionStorage-guarded against loops — to fetch the fresh shell (the
// no-cache index.html → new hashed entry → new chunks). The "New version
// available" banner usually preempts this; this covers the race where the user
// navigates to a fresh view before clicking Refresh.
export function recoverFromStaleChunks() {
  if (typeof window === 'undefined') return;
  const GUARD = 'sw-chunk-reload';

  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(event?.reason?.message ?? event?.reason ?? '');
    // Cross-browser dynamic-import failure messages:
    //   Chrome  "Failed to fetch dynamically imported module: <url>"
    //   Firefox "error loading dynamically imported module"
    //   Safari  "Importing a module script failed."
    if (!/dynamically imported module|module script failed|importing a module/i.test(msg)) return;
    try {
      if (sessionStorage.getItem(GUARD)) return;   // already reloaded once — don't loop
      sessionStorage.setItem(GUARD, '1');
    } catch { /* private mode / storage disabled — fall through to reload */ }
    location.reload();
  });

  // Clear the guard a few seconds after a successful load so a *future* deploy
  // can still trigger one recovery reload. The delay means that if the reload
  // we just did ALSO fails immediately (e.g. genuinely offline), the guard is
  // still set and we don't loop.
  window.addEventListener('load', () => {
    setTimeout(() => { try { sessionStorage.removeItem(GUARD); } catch { /* noop */ } }, 5000);
  });
}

function showBanner() {
  if (document.querySelector('.sw-update')) return;
  const banner = document.createElement('div');
  banner.className = 'sw-update';
  banner.setAttribute('role', 'status');
  banner.setAttribute('aria-live', 'polite');
  banner.innerHTML = `
    <span class="sw-update__msg">New version available</span>
    <button type="button" class="sw-update__btn" aria-label="Refresh to load the new version">Refresh</button>
  `;
  banner.querySelector('button').addEventListener('click', () => {
    location.reload();
  });
  document.body.appendChild(banner);
}
